import React, { useMemo, useEffect, useState } from 'react';
import proptypes from 'prop-types';
const { string, arrayOf, number, shape, func, bool } = proptypes;
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  Checkbox,
  LoadingButton, Typography
} from '@one-thd/sui-atomic-components';
import { IntentItem } from './IntentItem';
import { generateId } from '../../AddToHelpers';

export const GroupedIntents = ({ group, onItemUpdate, onBulkItemUpdate }) => {
  const { id, groupName, materials } = group;
  const [isGroupSelected, setIsGroupSelected] = useState(true);

  // Calculate if all items are selected
  useEffect(() => {
    const selectStatus = materials.every((item) => item.isSelected !== false);
    setIsGroupSelected(selectStatus);
  }, [materials]);

  // Calculate if some items are selected (for indeterminate state)
  const isIndeterminate = useMemo(() => {
    const selectedItems = materials.filter((item) => item.isSelected !== false);
    return selectedItems.length > 0 && selectedItems.length < materials.length;
  }, [materials]);

  const handleItemUpdate = (itemId, updatedFields) => {
    onItemUpdate(id, itemId, updatedFields);
  };

  const handleGroupSelectionChange = (event) => {
    const newIsSelected = event.target.checked;
    setIsGroupSelected(newIsSelected);
    const changedItems = materials.reduce((acc, item) => {
      acc[item.id] = { isSelected: newIsSelected };
      return acc;
    }, {});
    onBulkItemUpdate(id, changedItems);
  };

  const handleAddItem = () => {
    const newItemId = generateId();
    const newItem = {
      id: newItemId,
      itemId: null,
      intent: '',
      quantity: 1,
      isSelected: true,
      itemOrder: (materials?.length || 0) + 1
    };

    // Add the new item to this group
    onItemUpdate(id, newItemId, newItem);
  };

  return (
    <div
      className="sui-w-full"
    >
      <Accordion noBorders>
        <div
          className="sui-flex sui-items-center sui-gap-2 sui-border-1
          sui-border-primary sui-border-solid sui-rounded-md sui-pr-2
          sui-mx-2 sui-pl-2"
        >
          <Checkbox
            checked={isGroupSelected}
            indeterminate={isIndeterminate}
            onChange={handleGroupSelectionChange}
            id={`select-group-${id}`}
            aria-label={`Select all items in ${groupName}`}
          />
          <div className="sui-w-full">
            <AccordionHeader
              aria-controls={`accordion-${id}`}
              id={`accordion-header-${id}`}
            >
              {groupName}
            </AccordionHeader>
          </div>
        </div>
        <AccordionBody>
          <div className="sui-px-2 sui-pb-2">
            {
              materials?.length > 0 && (
                <div
                  className="sui-flex sui-justify-between sui-items-center sui-gap-2 sui-px-2 sui-pl-14"
                >
                  <div className="sui-flex-1">
                    <Typography variant="body-base" weight="bold">Item</Typography>
                  </div>
                  <div className="sui-w-20 sui-text-center">
                    <Typography variant="body-base" weight="bold">Qty</Typography>
                  </div>
                </div>
              )
            }

            {
              materials.map((item) => (
                <IntentItem
                  key={`grouped-item-${item.id}`}
                  item={item}
                  onUpdate={handleItemUpdate}
                />
              ))
            }
            <div
              className="sui-flex sui-justify-between sui-items-center sui-gap-2 sui-px-2 sui-mt-2 sui-pl-14"
            >
              <div className="sui-flex-1">
                <LoadingButton
                  loading={false}
                  variant="ghost"
                  onClick={handleAddItem}
                >
                  + Add Item
                </LoadingButton>
              </div>
            </div>
          </div>
        </AccordionBody>
      </Accordion>
    </div>
  );
};

GroupedIntents.displayName = 'GroupedIntents';

GroupedIntents.propTypes = {
  group: shape({
    id: string.isRequired,
    groupName: string.isRequired,
    materials: arrayOf(shape({
      id: string.isRequired,
      name: string,
      intent: string,
      quantity: number.isRequired,
      isSelected: bool
    })).isRequired
  }).isRequired,
  onItemUpdate: func.isRequired,
  onBulkItemUpdate: func.isRequired
};
